import React, {ReactElement} from 'react';
import {Input} from '@progress/kendo-react-inputs';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import styles from './_indirect-quoter-column.module.scss';
import {useSelector} from "react-redux";
import {hasValue, NoScrollNumericTextBox} from '@amsanalytics/react-shared-library';
import {Error, Label} from "@progress/kendo-react-labels";
import { AppState } from '../../../../store/rootReducer';
import {
  IndirectOnChangeFunction, 
  IndirectQuoteFieldType,
  IndirectQuoterColumnConfig,
  IndirectQuoterFieldConfig,
  IndirectQuoterInputType
} from "../../../../Interfaces/IndirectQuoter/IndirectQuoterFieldConfig";
import {IApplication} from "../../../../Interfaces/Application/IApplication";

interface IndirectQuoterProps {
  columnConfig: IndirectQuoterColumnConfig;
  showUseRecommendedButton: boolean;
  headerActions?: ReactElement;
  onChange: IndirectOnChangeFunction;
}

export default function IndirectQuoterColumn(props: IndirectQuoterProps) {
  const currentApp = useSelector((state: AppState) => state.applications.editingApp!);
  
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>{props.columnConfig.columnTitle}</h2>
        <div className={styles.headerControls}>
          {props.headerActions}
        </div>
      </div>
      
      {/* Input Rows */}
      <div className={styles.section}>
        <div className={styles.sectionContent}>
          {props.columnConfig.fieldConfigs.map((fieldConfig, key) => 
            <IndirectQuoterInput 
              key={key} 
              columnConfig={props.columnConfig}
              fieldConfig={fieldConfig}
              currentApp={currentApp}
              onChange={props.onChange}
            />
          )}
        </div>
      </div>
    </div>
  );
};

interface IndirectQuoterInputProps {
  columnConfig: IndirectQuoterColumnConfig;
  fieldConfig: IndirectQuoterFieldConfig;
  currentApp: IApplication;
  onChange: IndirectOnChangeFunction;
}

function IndirectQuoterInput(props: IndirectQuoterInputProps) {
  const underwriterValueLookup = props.fieldConfig.lookups && props.fieldConfig.lookups?.values?.find(v => v[props.fieldConfig.lookups!.idField] === props.fieldConfig.value);
  const calculatedValueLookup = props.fieldConfig.lookups && props.fieldConfig.lookups?.values?.find(v => v[props.fieldConfig.lookups!.idField] === props.fieldConfig.value);

  const calculatedValueDescription = calculatedValueLookup
    ? calculatedValueLookup[props.fieldConfig.lookups!.descriptionField] as string // The description of the lookup
    : undefined;
  
  const recommendedValueToDisplay = props.fieldConfig.inputType === IndirectQuoterInputType.Dropdown
    ? calculatedValueDescription
    : props.fieldConfig.recommendedValue;

  // Kendo's min only applies once the input commits, so a typed-in value below the floor is clamped up here as well
  const onNumericChange = (value: number | null | undefined) => {
    if (!hasValue(props.fieldConfig.fieldName)) {
      return;
    }

    const min = props.fieldConfig.min;
    const clampedValue = min !== undefined && hasValue(value) ? Math.max(min, value!) : value;
    props.onChange(props.columnConfig, props.fieldConfig.fieldName!, clampedValue);
  };

  const getInputComponent = (): ReactElement => {
    switch (props.fieldConfig.inputType) {
      case IndirectQuoterInputType.Percentage:
        return (
          <NoScrollNumericTextBox
            value={(props.fieldConfig.value as number | undefined) !== undefined
              ? (props.fieldConfig.value as number)
              : undefined}
            onChange={(e) => onNumericChange(e.target.value)}
            className={styles.inputField}
            disabled={props.fieldConfig.disableInput}
            valid={!hasValue(props.fieldConfig.errorMessage)}
            validationMessage={props.fieldConfig.errorMessage}
            //format="p2" // Percentage format with 2 decimal places
            spinners={false}
            min={props.fieldConfig.min}
          />
        );

      case IndirectQuoterInputType.Integer:
        return (
          <NoScrollNumericTextBox
            value={(props.fieldConfig.value as number | undefined) ?? 0}
            onChange={(e) => onNumericChange(e.target.value)}
            className={styles.inputField}
            disabled={props.fieldConfig.disableInput}
            valid={!hasValue(props.fieldConfig.errorMessage)}
            validationMessage={props.fieldConfig.errorMessage}
            format="n0" // Integer format with no decimal places
            spinners={false}
            min={props.fieldConfig.min}
          />
        );

      case IndirectQuoterInputType.Currency:
        return (
          <NoScrollNumericTextBox
            value={(props.fieldConfig.value as number | undefined) ?? 0}
            onChange={(e) => onNumericChange(e.target.value)}
            className={styles.inputField}
            disabled={props.fieldConfig.disableInput}
            valid={!hasValue(props.fieldConfig.errorMessage)}
            validationMessage={props.fieldConfig.errorMessage}
            format="c2" // Currency format with 2 decimal places
            spinners={false}
            min={props.fieldConfig.min}
          />
        );

      case IndirectQuoterInputType.Dropdown:
        if (!props.fieldConfig.lookups!.values) {
          console.error('Dropdown data is required for Dropdown type');
          return <div>Error: Dropdown data is required</div>;
        }

        return (
          <DropDownList
            data={props.fieldConfig.lookups!.values}
            textField={props.fieldConfig.lookups!.descriptionField}
            value={underwriterValueLookup}
            onChange={(e) => hasValue(props.fieldConfig.fieldName) && props.onChange(props.columnConfig, props.fieldConfig.fieldName!, e.target.value[props.fieldConfig.lookups!.idField])}
            className={styles.dropdownField}
            disabled={props.fieldConfig.disableInput}
            valid={!hasValue(props.fieldConfig.errorMessage)}
            validationMessage={props.fieldConfig.errorMessage}
          />
        );

      case IndirectQuoterInputType.Text:
      default:
        return (
          <Input
            value={props.fieldConfig.value as string | undefined}
            onChange={(e) => hasValue(props.fieldConfig.fieldName) && props.onChange(props.columnConfig, props.fieldConfig.fieldName!, e.target.value as string | undefined)}
            className={styles.inputField}
            disabled={props.fieldConfig.disableInput}
            valid={!hasValue(props.fieldConfig.errorMessage)}
            validationMessage={props.fieldConfig.errorMessage}
          />
        );
    }
  };

  const getFormattedRecommendedValue = (value: IndirectQuoteFieldType): string => {
    if (!props.fieldConfig.recommendedValue || !hasValue(value)) {
      return '';
    }

    switch (props.fieldConfig.inputType) {

      case IndirectQuoterInputType.Integer:
        // Format as integer with no decimal places
        return new Intl.NumberFormat('en-US', {
          style: 'decimal',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(value as number);

      case IndirectQuoterInputType.Currency:
        // Format as currency with 2 decimal places
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD', // Default to USD, could be parameterized
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(value as number);

      case IndirectQuoterInputType.Percentage:
        return (value as number).toFixed(2) + '%';
        
      case IndirectQuoterInputType.Dropdown:
      case IndirectQuoterInputType.Text:
      default:
        // For text, simply convert to string
        return String(value);
    }
  };
  
  const showErrorComponent = hasValue(props.fieldConfig.errorMessage);
  const showErrorSpacing = !showErrorComponent && props.fieldConfig.showErrorSpacing;  
  
  return (
    <div>
      <div className={styles.fieldHeader}>
        <Label className={hasValue(props.fieldConfig.errorMessage) ? styles.invalidFieldLabel : styles.fieldLabel}>
          {`${props.fieldConfig.fieldLabel} ${props.fieldConfig.fieldLabelAddOn ?? ''}`.trim()}
        </Label>
        {!props.columnConfig.hideAllRecommendedValues && !props.fieldConfig.hideRecommendation && // Hides if the whole column's recommended values are hidden or just this field
          <span className={styles.recommendedButton}>
            {getFormattedRecommendedValue(recommendedValueToDisplay)}
          </span>
        }
      </div>
      {getInputComponent()}
      {showErrorComponent && 
        <Error className={styles.errorMessage}>{props.fieldConfig.errorMessage}</Error>
      }
      {showErrorSpacing && 
        <div style={{height: "18px"}}></div> // adds spacing if the error message is showing on another column
      } 
    </div>
  );
}